import { Injectable } from "@angular/core";
import { HttpParams } from "@angular/common/http";
import { catchError, EMPTY, map, Observable, throwError } from "rxjs";

import { HttpClientHelperService } from "./http-client-helper.service";
import { CustomValuesQuery } from "../company-settings/state/custom-values/custom-values.query";
import { CustomFieldsQuery } from "../company-settings/state/custom-fields/custom-fields.query";
import { AssessmentActivityLogsResponse, AssessmentAttachment, AssessmentAuditFilterOptionsResponse, AssessmentCommentRequest, AssessmentCommentResponse, AssessmentCommentsEditRequest, AssessmentEditRequest, AssessmentLockStatus, AssessmentPageRequest, AssessmentPageResponse, AssessmentQuestionMoveRequest, AssessmentQuestionRequest, AssessmentQuestionResponse, AssessmentResourceId, AssessmentSectionRequest, AssessmentSectionResponse, AssessmentsViewsCountsResponse, AssessmentUpdateReviewDateRequest } from "./models/assessments/assessment.interface";
import { AssessmentBulkEditRequest, AssessmentBulkInviteRequest, AssessmentBulkResponse } from "./models/assessments/assessment-bulk.interface";
import { AssessmentsTemplate, AssessmentsTemplateRequest, AssessmentsTemplatesResponse } from "./models/assessments/assessment-template.interface";
import { CollaboratorVisibilityScopeEnum } from "./models/assessments/assessment.enum";
import { AssessmentsTemplateParserService } from "./assessments-template-parser.service";
import { AssessmentEditOpEnum, AssessmentStatusEnum } from "./models/assessments/assessment.enum";
import { RandomUUIDPipe } from "../shared/pipes/random-uuid.pipe";

@Injectable({
  	providedIn: 'root'
})
export class BaseApiClientAssessmentService {
    private readonly controllerV4 = 'assessment/v4/assessment-page';
    private readonly templatesController = 'assessment/v2/template-page';
	private readonly controllerV2 = 'assessment/v2/assessment-page';

    constructor(
        protected httpClient: HttpClientHelperService,
        protected customFieldsQuery: CustomFieldsQuery,
        protected customValuesQuery: CustomValuesQuery,
        protected assessmentsTemplateParserService: AssessmentsTemplateParserService,
        protected randomUUIDPipe: RandomUUIDPipe,
    ) {}

    // Assessment Page
    getAssessments(): Observable<AssessmentPageResponse[]> {
        return this.httpClient.invokeGetAuth<AssessmentPageResponse[]>(`${this.controllerV4}`);
    }

	getAssessment(assessmentPageId: AssessmentResourceId): Observable<AssessmentPageResponse> {
		return this.httpClient.invokeGetAuth<AssessmentPageResponse>(`${this.controllerV4}/${assessmentPageId}`);
	}

    updateAssessment(assessmentPageId: AssessmentResourceId, request: AssessmentEditRequest[]): Observable<AssessmentPageResponse> {
        return this.httpClient.invokePatchAuth<AssessmentPageResponse>(`${this.controllerV4}/${assessmentPageId}`, request);
    }

    bulkEditAssessments(request: AssessmentBulkEditRequest): Observable<AssessmentBulkResponse> {
        return this.httpClient.invokePatchAuth<AssessmentBulkResponse>(`${this.controllerV4}/bulk`, request);
    }

    /** Bulk invite — one collaborator across the selection. Per-assessment outcomes come back in the 200. */
    bulkInviteCollaborator(request: AssessmentBulkInviteRequest): Observable<AssessmentBulkResponse> {
        return this.httpClient.invokePostAuth<AssessmentBulkResponse>(`${this.controllerV4}/bulk/collaborator`, request);
    }

    deleteAssessment(assessmentPageId: AssessmentResourceId): Observable<void> {
        return this.httpClient.invokeDeleteAuth<void>(`${this.controllerV4}/${assessmentPageId}`);
    }

	createAssessment(request: AssessmentPageRequest): Observable<AssessmentPageResponse> {
		return this.httpClient.invokePostAuth<AssessmentPageResponse>(`${this.controllerV4}`, request);
	}

    cloneAssessment(assessmentPageId: AssessmentResourceId, name: string): Observable<AssessmentPageResponse>  {
        return this.httpClient.invokePostAuth<AssessmentPageResponse>(`${this.controllerV4}/duplicate`, { assessmentPageId, name });
    }

    // Assessment Section
    getAssessmentSections(assessmentPageId: AssessmentResourceId): Observable<AssessmentSectionResponse> {
        return this.httpClient.invokeGetAuth<AssessmentSectionResponse>(`${this.controllerV4}/${assessmentPageId}/section`);
    }

    getAssessmentSection(assessmentPageId: AssessmentResourceId, sectionId: AssessmentResourceId): Observable<AssessmentSectionResponse> {
        return this.httpClient.invokeGetAuth<AssessmentSectionResponse>(`${this.controllerV4}/${assessmentPageId}/section/${sectionId}`);
    }

    updateAssessmentSection(assessmentPageId: AssessmentResourceId, sectionId: AssessmentResourceId, request: AssessmentEditRequest[]): Observable<AssessmentSectionResponse> {
        return this.httpClient.invokePatchAuth<AssessmentSectionResponse>(`${this.controllerV4}/${assessmentPageId}/section/${sectionId}`, request);
    }

    deleteAssessmentSection(assessmentPageId: AssessmentResourceId, sectionId: AssessmentResourceId): Observable<AssessmentSectionResponse> {
        return this.httpClient.invokeDeleteAuth<AssessmentSectionResponse>(`${this.controllerV4}/${assessmentPageId}/section/${sectionId}`);
    }

    createAssessmentSection(assessmentPageId: AssessmentResourceId, request: AssessmentSectionRequest): Observable<AssessmentSectionResponse> {
        return this.httpClient.invokePostAuth<AssessmentSectionResponse>(`${this.controllerV4}/${assessmentPageId}/section`, request);
    }
    
    // Assessment Question
    getAssessmentQuestions(assessmentPageId: AssessmentResourceId, sectionId: AssessmentResourceId): Observable<AssessmentQuestionResponse[]> {
        return this.httpClient.invokeGetAuth<AssessmentQuestionResponse[]>(`${this.controllerV4}/${assessmentPageId}/section/${sectionId}/question`);
    }

    getAssessmentQuestion(assessmentPageId: AssessmentResourceId, sectionId: AssessmentResourceId, questionId: AssessmentResourceId): Observable<AssessmentQuestionResponse> {
        return this.httpClient.invokeGetAuth<AssessmentQuestionResponse>(`${this.controllerV4}/${assessmentPageId}/section/${sectionId}/question/${questionId}`);
    }

    updateAssessmentQuestion(
        assessmentPageId: AssessmentResourceId, 
        sectionId: AssessmentResourceId, 
        questionId: AssessmentResourceId, 
        request: AssessmentEditRequest[]
    ): Observable<AssessmentQuestionResponse> {
        return this.httpClient.invokePatchAuth<AssessmentQuestionResponse>(`${this.controllerV4}/${assessmentPageId}/section/${sectionId}/question/${questionId}`, request);
    }

    deleteAssessmentQuestion(assessmentPageId: AssessmentResourceId, sectionId: AssessmentResourceId, questionId: AssessmentResourceId): Observable<AssessmentQuestionResponse> {
        return this.httpClient.invokeDeleteAuth<AssessmentQuestionResponse>(`${this.controllerV4}/${assessmentPageId}/section/${sectionId}/question/${questionId}`);
    }

    createAssessmentQuestion(assessmentPageId: AssessmentResourceId, sectionId: AssessmentResourceId, request: AssessmentQuestionRequest): Observable<AssessmentQuestionResponse> {
        return this.httpClient.invokePostAuth<AssessmentQuestionResponse>(`${this.controllerV4}/${assessmentPageId}/section/${sectionId}/question`, request);
    }

    /**
     * Moves a question to a different section on the same page without changing its id.
     * Page-level action (spans two sections); ordering/logic are patched separately.
     */
    moveAssessmentQuestionToSection(assessmentPageId: AssessmentResourceId, request: AssessmentQuestionMoveRequest): Observable<AssessmentQuestionResponse> {
        return this.httpClient.invokePostAuth<AssessmentQuestionResponse>(`${this.controllerV4}/${assessmentPageId}/questions/move-section`, request);
    }

    getLockStatus(instanceId: AssessmentResourceId): Observable<AssessmentLockStatus> {
        return this.httpClient.invokeGetAuth<AssessmentLockStatus>(`${this.controllerV4}/${instanceId}/lock-status`);
    }

    setLockStatus(instanceId: AssessmentResourceId, isLocked: boolean): Observable<AssessmentLockStatus> {
        return this.httpClient.invokePost<AssessmentLockStatus>(`${this.controllerV4}/${instanceId}/lock-status`, {
            isLocked
        });
    }

    setReviewDate(review: AssessmentUpdateReviewDateRequest): Observable<AssessmentPageResponse> {
        return this.httpClient.invokePostAuth<AssessmentPageResponse>(`${this.controllerV4}/${review.instanceId}/set-review`, {
            reviewDate: review.reviewDate,
            ownerId: review.ownerId,
            setNoFutureReminders: review.setNoFutureReminders
        });
    }

    revokeCollaborator(id: AssessmentResourceId, email: string): Observable<AssessmentPageResponse> {
        return this.httpClient.invokeDeleteAuth<any>(`${this.controllerV4}/${id}/collaborator`, { email });
    }   

    inviteCollaborator(id: AssessmentResourceId, email: string, message: string, visibilityScope?: CollaboratorVisibilityScopeEnum): Observable<AssessmentPageResponse> {
        const payload = { email };
        if (message) {
            payload['inviteMessage'] = message;
        }
        // Section visibility (DevSectionVisibility): only send the scope when provided (flag on), so the
        // request is byte-identical to today when the feature is off.
        if (visibilityScope) {
            payload['visibilityScope'] = visibilityScope;
        }
        return this.httpClient.invokePostAuth<any>(`${this.controllerV4}/${id}/collaborator`, payload);
    }

    /** Section visibility (DevSectionVisibility): change an active collaborator's access scope. */
    updateCollaboratorScope(id: AssessmentResourceId, email: string, visibilityScope: CollaboratorVisibilityScopeEnum): Observable<AssessmentPageResponse> {
        return this.httpClient.invokePatchAuth<any>(`${this.controllerV4}/${id}/collaborator`, { email, visibilityScope });
    }

    // Legacy v2 endpoints
	getAssessmentViewsCounts(): Observable<AssessmentsViewsCountsResponse> {
		return this.httpClient.invokeGetAuth<AssessmentsViewsCountsResponse>(`${this.controllerV2}/count`);
	}

    getTemplates(): Observable<AssessmentsTemplate[]> {
        return this.httpClient.invokeGetAuth<AssessmentsTemplatesResponse[]>(`${this.templatesController}`).pipe(
            map(templatePages => {
                try {
                    return templatePages.map(templatePage => this.assessmentsTemplateParserService.parseTemplatePageToResponse(templatePage));
                }
                catch (parseError) {
                    throw parseError;
                }
            }),
            catchError(error => throwError(() => error))
        );
    }

    updateTemplate(request: AssessmentsTemplate): Observable<void> {
        return this.httpClient.invokePutAuth<void>(`${this.templatesController}/${request.id}`, this.assessmentsTemplateParserService.parseTemplateRequestToPageV2(request)).pipe(
            map(() => void 0),
            catchError(error => throwError(() => error))
        );
    }

    createTemplate(request: AssessmentsTemplateRequest): Observable<AssessmentsTemplate> {
        return this.httpClient.invokePostAuth<AssessmentsTemplatesResponse>(`${this.templatesController}`, this.assessmentsTemplateParserService.parseTemplateRequestToPageV2(request)).pipe(
            map(templatePage => {
                try {
                    return this.assessmentsTemplateParserService.parseTemplatePageToResponse(templatePage);
                }
                catch (parseError) {
                    throw parseError;
                }
            }),
            catchError(error => throwError(() => error))
        );
    }

    deleteTemplate(id: string): Observable<void> {
        return this.httpClient.invokeDeleteAuth<void>(`${this.templatesController}/${id}`);
    }
    
    uploadInstanceAttachment(instanceId: AssessmentResourceId, file: File): Observable<AssessmentAttachment> {
        return this.httpClient.invokeUploadFileAuth<AssessmentAttachment>(`${this.controllerV2}/${instanceId}/attachment`, file);
    }

    getInstanceAttachmentUrl(instanceId: AssessmentResourceId, attachmentId: string): Observable<string> {
        return this.httpClient.invokeGetAuth<string>(`${this.controllerV2}/${instanceId}/attachment/${attachmentId}`);
    }

    deleteInstanceAttachment(instanceId: AssessmentResourceId, attachmentId: string): Observable<AssessmentAttachment> {
        return this.httpClient.invokeDeleteAuth<AssessmentAttachment>(`${this.controllerV2}/${instanceId}/attachment/${attachmentId}`);
    }

    submitInstance(assessmentPageId: AssessmentResourceId): Observable<AssessmentPageResponse> {
        return EMPTY;
    }

    updateInstanceState(instanceId: AssessmentResourceId, state: AssessmentStatusEnum): Observable<AssessmentPageResponse> {
        return this.httpClient.invokePatchAuth<AssessmentPageResponse>(`${this.controllerV2}/${instanceId}/state`, { state });
    }

    getAssessmentComments(assessmentPageId: AssessmentResourceId): Observable<AssessmentCommentResponse[]> {
        return this.httpClient.invokeGetAuth<AssessmentCommentResponse[]>(`${this.controllerV4}/${assessmentPageId}/notes`);
    }

    createComment(assessmentPageId: AssessmentResourceId, sectionId: AssessmentResourceId, questionId: AssessmentResourceId, comment: AssessmentCommentRequest): Observable<AssessmentCommentResponse> {
        return this.httpClient.invokePostAuth<AssessmentCommentResponse>(`${this.controllerV4}/${assessmentPageId}/section/${sectionId}/question/${questionId}/notes`, comment);
    }

    resolveComment(
        assessmentPageId: AssessmentResourceId, 
        sectionId: AssessmentResourceId, 
        questionId: AssessmentResourceId, 
        commentId: AssessmentResourceId, 
        isResolved: boolean
    ): Observable<AssessmentCommentResponse> {
        const request: AssessmentCommentsEditRequest = {
            op: AssessmentEditOpEnum.Replace,
            path: `isResolved`,
            value: isResolved
        };
        return this.httpClient.invokePatchAuth<AssessmentCommentResponse>(`${this.controllerV4}/${assessmentPageId}/section/${sectionId}/question/${questionId}/notes/${commentId}`, [request]);
    }

    getActivityLogs(
        assessmentPageId: AssessmentResourceId,
        options?: {
            questionId?: AssessmentResourceId;
            userId?: AssessmentResourceId;
            before?: string;
            after?: string;
            limit?: number;
        }
    ): Observable<AssessmentActivityLogsResponse> {
        let params = new HttpParams();

        if (options?.questionId) {
            params = params.set('QuestionId', String(options.questionId));
        }
        if (options?.userId) {
            params = params.set('user', String(options.userId));
        }
        if (options?.before) {
            params = params.set('before', options.before);
        }
        if (options?.after) {
            params = params.set('after', options.after);
        }
        if (typeof options?.limit === 'number') {
            params = params.set('limit', String(options.limit));
        }

        return this.httpClient.invokeGetAuth<AssessmentActivityLogsResponse>(
            `${this.controllerV4}/${assessmentPageId}/audit`,
            params
        );
    }

    getAssessmentAuditFilterOptions(assessmentPageId: AssessmentResourceId): Observable<AssessmentAuditFilterOptionsResponse> {
        return this.httpClient.invokeGetAuth<AssessmentAuditFilterOptionsResponse>(
            `${this.controllerV4}/${assessmentPageId}/audit/filter-options`
        );
    }
}